import {
  cn,
  focusHaloFlush,
  HapticSurface,
  panelCard,
  panelMotion,
  panelRow,
  panelRowQuiet,
  press,
  Skeleton,
  ThemeToggle,
} from "@pax/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useRouter } from "@tanstack/react-router";
import {
  CloudUpload,
  Coins,
  Images,
  LogOut,
  Settings,
  SunMoon,
  Trash2,
  UserRound,
  UserRoundPlus,
  UserRoundX,
  X,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { Soon } from "@/components/Soon";
import { myAssetsQuery } from "@/features/assets/queries";
import { formatCredits } from "@/features/credits/format";
import { creditBalanceQuery } from "@/features/credits/queries";
import { useHasEarnings } from "@/features/earnings/queries";
import { friendsQuery, PANEL_FRIEND_COUNT, profileStatsQuery } from "@/features/profiles/queries";
import { useAccountDeletion } from "../account-deletion";
import { useAccountDialog } from "../account-dialog";
import { type ActAsTarget, actAs, canActAs, isActingAs, stopActing } from "../act-as";
import { type Session, sessionQuery, signOut, useCurrentProfile } from "../queries";
import { AccountAvatar } from "./AccountAvatar";
import { ActAsControls, FindUserDialog } from "./ActAsControls";
import { ProfileBanner } from "./ProfileBanner";
import { ProfileImagesDialog } from "./ProfileImagesDialog";

/** The banner spans the account panel inside its padding, or the screen on a phone. */
const ACCOUNT_BANNER_SIZES = "(width < 40rem) calc(100vw - 24px), 268px";

/** Signed out there is nothing to put in a panel, so there is no panel. */
export function AccountPane({ onClose }: { onClose?: () => void }) {
  const { data: session } = useQuery(sessionQuery);
  if (!session?.user) return null;
  return <AccountBody session={session} onClose={onClose} />;
}

/** Who you are, which opens your profile images when there is a profile to change. */
function Identity({
  onPress,
  stacked = false,
  children,
}: {
  onPress?: () => void;
  /** Stacks the banner above the avatar and name, instead of a row. */
  stacked?: boolean;
  children: ReactNode;
}) {
  const layout = stacked ? "flex-col items-stretch" : "items-center gap-1.5";
  if (!onPress) return <div className={cn("flex min-w-0 flex-1", layout)}>{children}</div>;
  return (
    <button
      type="button"
      onClick={onPress}
      title="Change profile images"
      className={cn(
        "relative -m-1 flex min-w-0 flex-1 rounded-xl p-1 text-left hover:bg-foreground/[0.07]",
        layout,
        panelMotion,
        press,
        focusHaloFlush,
      )}
    >
      {children}
      <HapticSurface />
    </button>
  );
}

/** A group of rows on their own ground, which is how a panel is divided. */
function Group({ children }: { children: ReactNode }) {
  return <div className={`flex flex-col gap-0.5 p-1 ${panelCard}`}>{children}</div>;
}

/** One figure on its own ground: a quiet label above what it currently says. */
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className={`flex flex-col gap-1.5 p-3 ${panelCard}`}>
      <span className="text-xs leading-tight text-muted-foreground">{label}</span>
      <span className="text-lg leading-none font-medium tabular-nums">{value}</span>
    </div>
  );
}

/** Your friends, the first few of them, each a way to their profile. */
function MyFriends({ profileId, onClose }: { profileId: string; onClose?: () => void }) {
  const { data } = useQuery(friendsQuery(PANEL_FRIEND_COUNT));
  const friends = data?.items ?? [];

  return (
    <div>
      <div className="flex items-baseline justify-between px-1.5 pb-1.5">
        <p className="text-xs text-muted-foreground">My friends</p>
        {data?.nextCursor ? (
          <Link
            to="/profiles/$profileId"
            params={{ profileId }}
            onClick={onClose}
            className={`rounded text-xs text-muted-foreground hover:text-foreground ${focusHaloFlush}`}
          >
            See all
          </Link>
        ) : null}
      </div>
      <Group>
        {!data ? (
          <div className={panelRowQuiet}>
            <Skeleton className="size-6 shrink-0 rounded-full" />
            <Skeleton className="h-4 w-28" />
          </div>
        ) : friends.length === 0 ? (
          <div className={cn(panelRowQuiet, "text-muted-foreground")}>
            <UserRoundPlus aria-hidden className="size-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate">Add friends</span>
            <span className="hidden shrink-0 text-xs @[15rem]:block">From their profiles</span>
          </div>
        ) : (
          friends.map(({ profile }) => (
            <Link
              key={profile.profileId}
              to="/profiles/$profileId"
              params={{ profileId: profile.profileId }}
              onClick={onClose}
              className={panelRow}
            >
              <AccountAvatar
                name={profile.displayName}
                image={profile.avatar}
                size={24}
                className="shrink-0"
              />
              <span className="min-w-0 flex-1 truncate">{profile.displayName}</span>
              {profile.username ? (
                <span className="hidden min-w-0 shrink truncate text-xs text-muted-foreground @[15rem]:block">
                  @{profile.username}
                </span>
              ) : null}
              <HapticSurface />
            </Link>
          ))
        )}
      </Group>
    </div>
  );
}

/**
 * Your assets, and how many drafts are still waiting to be published. The
 * count is the first page of drafts, so past a page it says there are more
 * rather than counting them.
 */
function MyAssetsRow({ userId }: { userId: string }) {
  const { data } = useInfiniteQuery(myAssetsQuery("draft", userId));
  const first = data?.pages[0];
  const drafts = first ? `${first.assets.length}${first.nextCursor ? "+" : ""}` : "";
  return (
    <Link to="/assets/mine" className={panelRow}>
      <Images aria-hidden className="size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 truncate">My assets</span>
      {drafts !== "" && drafts !== "0" && (
        <span
          title={`${drafts} ${drafts === "1" ? "draft" : "drafts"}`}
          className="text-xs text-muted-foreground tabular-nums"
        >
          {drafts}
        </span>
      )}
      <HapticSurface />
    </Link>
  );
}

/**
 * Everything about you: who you are, the figures, the things you made, the
 * people you play with, and at the foot the three things you do to the app
 * rather than to your account.
 *
 * The places everyone goes are the ways, in the column. The only places in
 * here are the ones that are yours, such as your assets.
 *
 * It has to read at two widths — its own column beside the screen, and the
 * narrower one the sidebar is still widening through while it slides in — so
 * it measures the room it has rather than being told which it is in.
 */
function AccountBody({ session, onClose }: { session: Session; onClose?: () => void }) {
  const { user } = session;
  const profile = useCurrentProfile(user);
  const queryClient = useQueryClient();
  const router = useRouter();
  const navigate = useNavigate();
  const { openAccountDialog } = useAccountDialog();
  const [findUserOpen, setFindUserOpen] = useState(false);
  const [profileImagesOpen, setProfileImagesOpen] = useState(false);
  const { data: credits } = useQuery(creditBalanceQuery(user.id));
  const guest = user.isAnonymous === true;
  const { data: stats } = useQuery({ ...profileStatsQuery(user.id), enabled: !guest });
  const hasEarnings = useHasEarnings(user);
  const developer = canActAs(session);
  const acting = isActingAs(session);
  const profileLoading = !guest && profile.isPending;
  const displayName = guest ? "Guest" : (profile.data?.displayName ?? "Account");
  const banner = guest ? null : (profile.data?.banner ?? null);
  const openProfileImages = profile.data && !guest ? () => setProfileImagesOpen(true) : undefined;

  const switchUser = useMutation({
    mutationFn: async (target: ActAsTarget | null) => {
      if (target) await actAs(queryClient, target.id);
      else await stopActing(queryClient);
    },
    onSuccess: async () => {
      setFindUserOpen(false);
      onClose?.();
      await router.invalidate();
      await navigate({ to: "/", replace: true });
    },
  });

  // Signing out leaves you where you are. Invalidating is what sends you on
  // from a screen that turns out to need an account.
  async function handleSignOut() {
    try {
      await signOut(queryClient);
    } finally {
      await router.invalidate();
    }
  }

  function handleSaveProgress() {
    onClose?.();
    openAccountDialog();
  }

  return (
    <>
      <div className="@container flex min-h-0 flex-1 flex-col">
        <div className="relative flex min-w-0 shrink-0 items-center gap-1.5 px-3 pt-3 pb-4">
          {banner ? (
            <Identity onPress={openProfileImages} stacked>
              <span className="block aspect-[3/1] w-full overflow-hidden rounded-lg bg-foreground/5">
                <ProfileBanner banner={banner} sizes={ACCOUNT_BANNER_SIZES} />
              </span>
              <span className="-mt-6 flex min-w-0 items-start gap-2.5 px-2">
                <span
                  className={cn(
                    "shrink-0 rounded-full p-[3px]",
                    onClose ? "bg-popover" : "bg-background",
                  )}
                >
                  <AccountAvatar name={displayName} image={profile.data?.avatar} size={48} />
                </span>
                <span className="min-w-0 flex-1 pt-7">
                  <span className="block truncate text-sm font-medium">
                    {acting ? "Acting as " : null}
                    {displayName}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">{user.email}</span>
                </span>
              </span>
            </Identity>
          ) : (
            <Identity onPress={openProfileImages}>
              {profileLoading ? (
                <Skeleton className="size-9 shrink-0 rounded-full" />
              ) : (
                <AccountAvatar
                  name={displayName}
                  image={profile.data?.avatar}
                  size={36}
                  className="shrink-0"
                />
              )}
              <div className="ml-1 min-w-0 flex-1">
                {profileLoading ? (
                  <Skeleton className="my-0.5 h-4 w-28" />
                ) : (
                  <p className="truncate text-sm font-medium">
                    {acting ? "Acting as " : null}
                    {displayName}
                  </p>
                )}
                <p className="truncate text-xs text-muted-foreground">
                  {guest ? (acting ? "Temporary account" : "Not saved anywhere yet") : user.email}
                </p>
              </div>
            </Identity>
          )}
          {/* Where this is a sheet, the corner is the first place a thumb goes
            to put it away, so it has to find a close there. Over a banner it
            sits on the artwork. */}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close account"
              className={cn(
                "relative grid size-8 shrink-0 place-items-center rounded-full",
                banner
                  ? "absolute top-5 right-5 bg-background/70 text-foreground shadow-sm ring-1 ring-foreground/10 hover:bg-background/90"
                  : "text-muted-foreground hover:bg-foreground/[0.07] hover:text-foreground",
                panelMotion,
                press,
                focusHaloFlush,
              )}
            >
              <X aria-hidden className="size-4" />
              <HapticSurface />
            </button>
          )}
        </div>

        <div className="scroll-fade flex flex-1 flex-col gap-3 overflow-y-auto px-3 pb-3">
          {guest && !acting ? (
            <div className={`flex flex-col gap-2.5 p-3 ${panelCard}`}>
              <p className="text-xs text-pretty text-muted-foreground">
                Credits, friends, and everything you make belong to an account. Connect one and this
                guest keeps its games.
              </p>
              <button
                type="button"
                onClick={handleSaveProgress}
                className={cn(
                  "flex h-9 items-center justify-center gap-2 rounded-lg bg-foreground text-sm font-medium text-background",
                  panelMotion,
                  press,
                  focusHaloFlush,
                )}
              >
                <CloudUpload aria-hidden className="size-4" />
                Save progress
              </button>
            </div>
          ) : !guest ? (
            <>
              <div className="grid grid-cols-1 gap-2 @[15rem]:grid-cols-2">
                <Stat
                  label="Credits"
                  value={credits === undefined ? "–" : formatCredits(credits)}
                />
                <Stat
                  label="Rounds played"
                  value={stats === undefined ? "–" : stats.roundsPlayed.toLocaleString()}
                />
              </div>

              <Group>
                <Link
                  to="/profiles/$profileId"
                  params={{ profileId: user.id }}
                  onClick={onClose}
                  className={panelRow}
                >
                  <UserRound aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate">My profile</span>
                  {profile.data ? (
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {presetCount(profile.data.publishedPresetCount)}
                    </span>
                  ) : null}
                  <HapticSurface />
                </Link>
                <MyAssetsRow userId={user.id} />
                {hasEarnings && (
                  <Link to="/earnings" onClick={onClose} className={panelRow}>
                    <Coins aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate">Preset earnings</span>
                    <HapticSurface />
                  </Link>
                )}
              </Group>

              <MyFriends profileId={user.id} onClose={onClose} />
            </>
          ) : null}

          {!acting && (
            <Group>
              <DeleteAccountRow onClose={onClose} />
            </Group>
          )}

          {developer ? (
            <div>
              <p className="px-1.5 pb-1.5 text-xs text-muted-foreground">Act as</p>
              <Group>
                <ActAsControls
                  pending={switchUser.isPending}
                  onActAs={target => switchUser.mutate(target)}
                  onFindUser={() => {
                    switchUser.reset();
                    setFindUserOpen(true);
                  }}
                />
              </Group>
            </div>
          ) : null}
        </div>

        {/* The things you do to the app rather than to your account, pinned to
          the foot and out of the way of everything you came here to read. */}
        <div className="shrink-0 px-3 pb-3">
          <Group>
            {acting ? (
              <button
                type="button"
                disabled={switchUser.isPending}
                onClick={() => switchUser.mutate(null)}
                className={cn(panelRow, "disabled:pointer-events-none disabled:opacity-50")}
              >
                <UserRoundX aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate">Stop acting</span>
                <HapticSurface />
              </button>
            ) : null}
            <div className={cn(panelRowQuiet, "text-muted-foreground")}>
              <Settings aria-hidden className="size-4 shrink-0" />
              <span className="min-w-0 flex-1 truncate">Settings</span>
              <Soon />
            </div>
            <div className={panelRowQuiet}>
              <SunMoon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">Theme</span>
              <ThemeToggle className="-mr-1.5" />
            </div>
            <button
              type="button"
              disabled={switchUser.isPending}
              onClick={() => void handleSignOut()}
              className={cn(panelRow, "disabled:pointer-events-none disabled:opacity-50")}
            >
              <LogOut aria-hidden className="size-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">Sign out</span>
              <HapticSurface />
            </button>
          </Group>
          {switchUser.error && !findUserOpen ? (
            <p role="alert" className="px-2.5 pt-2 text-xs text-destructive">
              {switchUser.error.message}
            </p>
          ) : null}
        </div>
      </div>

      {profile.data && !guest ? (
        <ProfileImagesDialog
          profile={profile.data}
          open={profileImagesOpen}
          onOpenChange={setProfileImagesOpen}
        />
      ) : null}

      {developer ? (
        <FindUserDialog
          open={findUserOpen}
          pending={switchUser.isPending}
          onOpenChange={setFindUserOpen}
          onActAs={target => switchUser.mutate(target)}
          error={switchUser.error?.message ?? null}
        />
      ) : null}
    </>
  );
}

/** Opens account deletion. */
function DeleteAccountRow({ onClose }: { onClose?: () => void }) {
  const { openAccountDeletion } = useAccountDeletion();
  return (
    <button
      type="button"
      onClick={() => {
        onClose?.();
        openAccountDeletion();
      }}
      className={panelRow}
    >
      <Trash2 aria-hidden className="size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 truncate">Delete account</span>
      <HapticSurface />
    </button>
  );
}

function presetCount(count: number): string {
  return count === 1 ? "1 preset" : `${count.toLocaleString()} presets`;
}
